<template>
  <div class="container">
    <div class="header">
      <button class="invite-button" @click.prevent="openInviteUserModal">invite</button>
    </div>
    <div class="user-management">
      <div class="" v-if="isDataFetching">
        <div class="user-management__loader">
          <base-loader/>
        </div>
      </div>
      <div class="common-table user-management" v-else>
        <div class="common-table__row common-table__header">
          <div class="common-table__header-cell"
               v-for="column in usersTableColumnsConfig"
               :key="column.property"
               :class="{
              'common-table__header-cell_sortable': column.isSortable,
              [`user-management__${column.property}`]: true
            }"
               @click="onHeaderCellClicked($event, column)">
            <base-table-sortable-column
              v-if="column.isSortable"
              :label="column.label"
              :prop-name="column.property"
              :sort-config="sortConfig"/>
            <template v-else>{{column.label}}</template>
          </div>
        </div>
        <template v-if="dataMissing">
          <div class="common-table__row no-data-block">
            No records found
          </div>
        </template>
        <div v-else
             class="common-table__row"
             v-for="(user) in tableData"
             :key="user._id">
          <div class="common-table__cell"
               v-for="column in usersTableColumnsConfig"
               :key="column.property"
               :class="`user-management__${column.property}`">
            <template v-if="column.isSelectableValue">
              <template v-if="column.isMine">
                <span v-if="user.role?.toLowerCase() === 'administrators'">{{user.user_role}}</span>
                <base-select
                  v-else
                  class="user-management__select"
                  :options="column.options"
                  :border-less="true"
                  alignLeft="true"
                  showTopBorder="true"
                  shiftLabelRight="true"
                  :disabled="currentUser._id === user._id"
                  v-model="user.role_id"
                  :selectStyle="{height: '60px!important'}"
                  @change="onSelect(user, column.property)"/>
              </template>
              <template v-else>
                <base-select
                  class="user-management__select"
                  :options="column.options"
                  :border-less="true"
                  alignLeft="true"
                  showTopBorder="true"
                  shiftLabelRight="true"
                  :disabled="currentUser._id === user._id"
                  v-model="user[column.property]"
                  :selectStyle="{height: '60px!important'}"
                  @change="onSelect(user, column.property)"/>
              </template>
            </template>
            <template v-else><span class="user-management__label">{{getColumnData(user, column.property)}}</span>
            </template>
          </div>
        </div>

      </div>
      <div class="" v-if="isUserUpdating || isUserInviting">
        <div class="user-management__update-loader">
          <base-loader class="user-management__update-loader-ring"/>
        </div>
      </div>
      <div class="user-management__footer_wrapper"
           v-if="listOfUsers.length > pageLengthLimit">
        <base-pagination class="user-management__pagination"
                         :totalItems="listOfUsers.length"
                         :limit="pageLengthLimit"
                         @pageChanged="setPaginationProps">
        </base-pagination>
      </div>

      <modal name="invite"
             v-bind="inviteUserModalProps"
      >
        <InviteUserPopup
          :roles="listOfInviteRoles"
          @invite="onInviteUser"
          @cancel="onCancelInvite"/>
      </modal>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState } from 'vuex'
import _ from 'lodash'
import InviteUserPopup from './InviteUserPopup'
import helper from '@/services/Helper'
import baseModalProps from '@/common/BaseModalProps'

const PAGE_LENGTH_LIMIT = 20
/**
 * Component allows managing users and their roles
 */
export default {
  name: 'UserManagement',
  components: {
    InviteUserPopup
  },
  data: function () {
    return {
      listOfRoles: [],
      listOfInviteRoles: [],
      sortedListOfUsers: [],
      isDataFetched: false,
      isDataUpdating: false,
      sortConfig: [ {
        propertyName: 'user_role',
        sortDirection: helper.DIRECTION_ASC,
        valuePreFormatter: null
      } ],
      paginationProps: { from: 0, to: PAGE_LENGTH_LIMIT },
      // table config
      usersTableColumnsConfig: [
        {
          label: 'Name',
          property: 'name',
          isSortable: true
        },
        // {
        //   label: 'Email',
        //   property: 'email',
        //   isSortable: false
        // },
        {
          label: 'Created',
          property: 'confirmed_at',
          isSortable: true,
          valuePreFormatter: (timestamp) => { // preformatter for sorting loginc
            if (!timestamp) {
              return null
            }
            return moment(timestamp).valueOf()
          }
        },
        {
          label: 'Project role',
          property: 'user_role',
          isMine: true,
          isSortable: true,
          isSelectableValue: true,
          options: []
        }
      ]
    }
  },
  watch: {
    selectedClient () {
      this.paginationProps = { from: 0, to: PAGE_LENGTH_LIMIT }
      this.selectedClient && this.fetchUsers(this.clientId)
    },
    listOfUsers () {
      this.updateSortedBriefsList()
    },
    roles () {
      this.initRoles()
    }
  },
  created: async function () {
    this.selectedClient && this.fetchUsers(this.clientId)
    this.sortedListOfUsers = this.listOfUsers && this.listOfUsers
    this.initRoles()
  },
  computed: {
    ...mapState({
      currentUser: state => state.currentUser,
      selectedClient: state => state.selectedClient,
      listOfUsers: state => state.users.users,
      roles: state => state.roles,
      isDataFetching: state => state.users.isDataFetching,
      isUserUpdating: state => state.users.isUserUpdating,
      isUserInviting: state => state.users.isUserInviting
    }),
    tableData () {
      // current user should be at the top of list
      let me = null
      if (this.currentUser) {
        me = this.sortedListOfUsers.find(user => user._id === this.currentUser._id)
      }

      const result = this.sortedListOfUsers.filter(user => user._id !== this.currentUser._id).slice(this.paginationProps.from, this.paginationProps.to)

      return me
        ? [ me, ...result ]
        : result
    },
    dataMissing () {
      return this.isDataFetched && !this.tableData.length
    },
    pageLengthLimit () {
      return PAGE_LENGTH_LIMIT
    },
    changeStatusModalProps: function () {
      const changeStatusModalProps = {
        width: 666
      }
      return { ...baseModalProps, ...changeStatusModalProps }
    },
    inviteUserModalProps() {
      return { ...baseModalProps, width: 500 }
    }
  },
  methods: {
    ...mapActions('users', [
      'getUsersByClient',
      'updateUserStatus',
      'updateUserRole',
      'inviteUser'
    ]),
    initRoles () {
      this.listOfRoles = this.roles.map(role => Object.assign({}, { value: role._id, label: role.label, name: role.name }))
      this.listOfInviteRoles = this.listOfRoles.filter(role => !['Agency Admin'].includes(role.label))
      this.usersTableColumnsConfig.find(item => item.property === 'user_role').options = [
        {
          value: null,
          name: null,
          label: 'Unassigned'
        },
        ...this.listOfInviteRoles
      ]
    },
    setPaginationProps (props) {
      this.paginationProps = props
    },
    fetchUsers: async function (clientId) {
      await this.getUsersByClient({ clientId })
      !this.isDataFetched && (this.isDataFetched = true)
    },
    updateRole: function (userId, clientId, roleId, roleLabel) {
      this.updateUserRole({ userId, clientId, roleId, roleLabel })
    },
    /**
     * Handling click on header of column, if
     * @param {Object} event
     * @param {Object} config - column header config
     * Calls {@link #changeSorting()} if column is sortable
     */
    onHeaderCellClicked (event, config) {
      if (!config.isSortable) {
        return
      }
      this.changeSorting(event, config)
    },
    /**
     * Changing sorting direction for column
     * @param {Object} event
     * @param {Object} config - column header config
     */
    changeSorting (event, columnConfig) {
      const isMultiple = event.shift
      const propName = columnConfig.property
      const currentConfigIndex = this.sortConfig.findIndex(config => {
        return config.propertyName === propName
      })

      //  Works when next sort type is 'ASC'
      if (!this.sortConfig.length || currentConfigIndex < 0) {
        const newSortConfig = getNewSortConfig(columnConfig)
        this.sortConfig = isMultiple ? [ ...this.sortConfig, newSortConfig ] : [ getNewSortConfig(columnConfig) ]
        this.updateSortedBriefsList()
        return
      }

      const currentSortDirection = this.sortConfig[ currentConfigIndex ].sortDirection
      const newSortDirection = helper.getNewSortDirection(currentSortDirection)
      // Next code off sorting
      if (!newSortDirection) {
        this.sortConfig.splice(currentConfigIndex, 1)
      } else {
        //  If sort type is 'DESC' update sortable column with ne sort type
        this.sortConfig[ currentConfigIndex ].sortDirection = newSortDirection
        this.sortConfig = isMultiple ? [ ...this.sortConfig ] : [ this.sortConfig[ currentConfigIndex ] ]
      }
      this.updateSortedBriefsList()
    },
    updateSortedBriefsList () {
      this.sortedListOfUsers = this.sortConfig.length ? helper.sortByMultipleParams(this.listOfUsers, this.sortConfig) : this.listOfUsers
    },
    getColumnData (user, property) {
      if (property === 'confirmed_at') {
        return user[ property ] ? moment(user[ property ]).format('MMM DD, YYYY') : 'Unconfirmed'
      }
      return user[ property ]
    },
    /**
     * Handling selecting user role or his status
     */
    onSelect (user, columnProperty) {
      if (columnProperty === 'user_role') {
        const newRoleLabel = this.roles.find(role => role._id === user.role_id)?.label
        this.updateRole(user._id, this.clientId, user.role_id, newRoleLabel)
      }
    },
    /**
     * Shows {@link ./InviteUserPopup}
     */
    openInviteUserModal () {
      this.$modal.show('invite')
    },
    /**
     * Handling when {@link ./InviteUserPopup} emit canceling invitation new user
     */
    onCancelInvite () {
      this.$modal.hide('invite')
    },
    /**
     * Handling when {@link ./InviteUserPopup} emit inviting new user
     */
    onInviteUser: async function (inviteData) {
      this.$modal.hide('invite')
      this.inviteUser({ ...inviteData, clientId: this.clientId })
    }
  }
}

/**
 *return new sort direction
 */
function getNewSortConfig (columnConfig) {
  return {
    propertyName: columnConfig.property,
    sortDirection: helper.DIRECTION_ASC,
    valuePreFormatter: columnConfig.valuePreFormatter || null
  }
}
</script>
<style scoped lang="scss">

  h1 {
    margin: 24px 0 24px;
  }

  .header {
    display: flex;
    justify-content: flex-end;
  }

  .invite-button {
    position: relative;
    display: inline-block;
    text-align: right;
    padding: 0 17px;
    width: 123px;
    border: none;
    height: 50px;
    background-color: $rfx-medium-march;
    margin: 15px 0;
    border-radius: 4px;
    text-transform: uppercase;
    color: white;
    font-size: 13px;
    font-weight: 500;

    &:before, &:after {
      position: absolute;
      left: 25px;
      top: 17px;
      content: "";
      height: 15px;
      width: 2px;
      background-color: white;
    }

    &:before {
      transform: rotate(90deg);
    }

    &:after {
      transform: rotate(180deg);
    }
  }

  .invite-button:hover {
    cursor: pointer;
  }

  .invite-button:focus {
    outline: 0;
  }

  .common-table {
    border-radius: 4px;
    overflow: visible;

    &__row {
      background-color: #fff !important;
      border-left: 1px solid $rfx-light-steel;
      border-right: 1px solid $rfx-light-steel;

      &:last-child {
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
      }

      &:first-child {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
      }
    }

    &__header {
      border-top-right-radius: 4px;
      border-top-left-radius: 4px;
      background-color: rgba(1, 22, 39, 0.1) !important;
    }
    &__header-cell {
      color: $rfx-dark-march;
      font-weight: bold;
      padding: 0 20px;
      height: 60px;
      display: flex;
      align-items: center;
      font-size: 14px;

      &:first-child {
        padding-left: 30px;
      }

      &:last-child {
        padding-right: 30px;
      }

      .sort-column {
        width: -webkit-fill-available;
      }
    }

    &__cell {
      color: $rfx-dark-march;
      height: 60px;
      padding: 0 20px;
      display: flex;
      align-items: center;
      font-size: 14px;
      border-bottom: 1px solid $rfx-light-steel;

      &:first-child {
        padding-left: 30px;
      }
      &:last-child {
        padding-right: 30px;
      }
    }

    &__row:last-child:not(&__header) {
      border-bottom-left-radius: 4px;
      border-bottom-right-radius: 4px;
    }

    &__row:last-child:not(&__header) >  &__cell:first-child{
      border-bottom-left-radius: 4px;
    }

    &__row:last-child:not(&__header) >  &__cell:last-child{
       border-bottom-right-radius: 4px;
    }
  }

  .user-management {

    &__footer_wrapper {
      display: flex;
      justify-content: center;
    }

    &__label {
      @include ellipsis
    }

    &__pagination {
      height: 60px;
    }

    &__loader {
      display: flex;
      width: 100%;
      justify-content: center;
      align-items: center;
      height: calc(100vh - 530px);
    }

    &__update-loader {
      position: absolute;
      display: flex;
      width: 100%;
      height: 100%;
      justify-content: center;
      background-color: white;
      top: 0;
      opacity: 0.5;
    }

    &__update-loader-ring {
      margin-top: 200px;
    }

    &__no-data {
      display: flex;
      height: 300px;
      background-color: #fff;
      border-radius: $rfx-default-border-radius;
      align-items: center;
      justify-content: center;
      font-size: 27px;
      font-weight: 600;
      color: $rfx-medium-grey;
      flex: 1 1;
    }

    &__name {
      color: $rfx-dark-march;
      flex: 1 1 500px;
      overflow: hidden;
    }

    &__email {
      flex: 1 1 500px;
      overflow: hidden;
    }

    &__confirmed_at {
      flex: 0 0 100px;
    }

    &__select {
      flex: 1 1 100%;
      margin-left: -10px;
    }

    &__user_role {
      flex: 0 0 160px;
      display: flex;
    }
  }

  .pagination-wrapper {
    display: flex;
    height: 60px;
    justify-content: center;
    align-items: center;
    border-top: 2px solid $rfx-almost-white;
    border-bottom: 1px solid $rfx-almost-white;
  }
</style>
